import { useEffect, useRef, useState } from "react"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuAction,
} from "~/react/ui/dropdown-menu"
import { Popover, PopoverTrigger, PopoverContent } from "~/react/ui/popover"
import { NumberPicker } from "~/react/ui/number-picker"
import { cn } from "~/lib/utils"
import { useCampaignContent } from "../campaign_content_context"
import type { FollowUp } from "../types"
import { t } from "../i18n"

interface CampaignStepCardProps {
  followUp: FollowUp
  isSelected: boolean
  onSelect: () => void
}

export function CampaignStepCard({
  followUp,
  isSelected,
  onSelect,
}: CampaignStepCardProps) {
  const [waitDaysPopoverOpen, setWaitDaysPopoverOpen] = useState(false)
  const waitDaysPickerWrapperRef = useRef<HTMLDivElement | null>(null)
  const { updateFollowUp } = useCampaignContent()

  const isOpeningEmail = followUp.step === 0

  // Helper to check if HTML body is empty (strips HTML tags and checks for actual content)
  const isBodyEmpty = (html: string): boolean => {
    if (!html) return true
    // Strip HTML tags and check if there's any text content
    const textContent = html.replace(/<[^>]*>/g, "").trim()
    return !textContent || textContent === ""
  }

  // Check if content is missing
  const hasMissingContent = isOpeningEmail
    ? !followUp.subject?.trim() || isBodyEmpty(followUp.body)
    : isBodyEmpty(followUp.body)

  useEffect(() => {
    if (!waitDaysPopoverOpen) return

    // Popover content is portalled; wait a tick to ensure it's mounted before focusing.
    const raf = requestAnimationFrame(() => {
      const input = waitDaysPickerWrapperRef.current?.querySelector(
        'input[type="number"]',
      ) as HTMLInputElement | null
      input?.focus()
    })

    return () => cancelAnimationFrame(raf)
  }, [waitDaysPopoverOpen])

  return (
    <div
      className={cn(
        "campaign-step-card",
        isSelected && "campaign-step-card--selected",
      )}
    >
      <button
        type="button"
        className="campaign-step-card__clickable-area"
        onClick={onSelect}
        aria-label={
          isOpeningEmail
            ? t("js.campaigns.content.opening_email")
            : t("js.campaigns.content.follow_up", { step: followUp.step })
        }
      />
      {/* Wait days row for follow-ups only */}
      {!isOpeningEmail && (
        <>
          <Popover
            open={waitDaysPopoverOpen}
            onOpenChange={setWaitDaysPopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="default"
                size="sm"
                fullWidth
                className="campaign-step-card__delay"
              >
                <Icon icon="timer" size="sm" aria-hidden="true" />
                <span className="campaign-step-card__wait-days-text">
                  {t("js.campaigns.content.wait_for")}{" "}
                  <strong>
                    {t("js.campaigns.content.days", {
                      count: followUp.wait_days,
                    })}
                  </strong>
                </span>
                <Icon
                  icon="chevron-down"
                  size="xs"
                  className="campaign-step-card__chevron"
                  aria-hidden="true"
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="campaign-step-card__wait-days-popover"
              align="start"
            >
              <div className="campaign-step-card__wait-days-content">
                <div
                  className="campaign-step-card__wait-days-number-picker-wrapper"
                  ref={waitDaysPickerWrapperRef}
                >
                  <NumberPicker
                    size="sm"
                    value={followUp.wait_days}
                    onChange={(newWaitDays) => {
                      updateFollowUp(followUp.step, { wait_days: newWaitDays })
                    }}
                    min={1}
                    ariaLabelPrefix={t("js.campaigns.content.wait_days_label")}
                    className="campaign-step-card__wait-days-number-picker"
                  />
                  <span>{t("js.campaigns.content.day_s")}</span>
                </div>
                <p className="campaign-step-card__wait-days-description">
                  {t("js.campaigns.content.wait_days_description")}
                </p>
              </div>
            </PopoverContent>
          </Popover>
          <div className="campaign-step-card__separator" aria-hidden="true" />
        </>
      )}

      {/* Step info row */}
      <div className="campaign-step-card__content">
        <button
          type="button"
          className="campaign-step-card__select-button"
          aria-current={isSelected ? "step" : undefined}
          aria-controls="campaign-step-editor"
        >
          <div className="campaign-step-card__icon-wrapper">
            <Icon icon="envelope" size="sm" aria-hidden="true" />
          </div>
          <div className="campaign-step-card__title-wrapper">
            <span className="campaign-step-card__title">
              {isOpeningEmail
                ? t("js.campaigns.content.opening_email")
                : t("js.campaigns.content.follow_up", { step: followUp.step })}
            </span>
            {hasMissingContent && (
              <span className="campaign-step-card__missing-content">
                {t("js.campaigns.content.missing_content")}
              </span>
            )}
          </div>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              className="campaign-step-card__options-button"
              aria-label="Step options"
            >
              <Icon icon="ellipsis-vertical" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuAction type="button">
              <Icon
                icon="copy"
                size="sm"
                variant="regular"
                aria-hidden="true"
              />
              {t("js.campaigns.content.duplicate")}
            </DropdownMenuAction>
            {!isOpeningEmail && (
              <DropdownMenuAction type="button" variant="destructive">
                <Icon
                  icon="trash-alt"
                  size="sm"
                  variant="regular"
                  aria-hidden="true"
                />
                {t("js.campaigns.content.delete_step")}
              </DropdownMenuAction>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
